import React from 'react';
import {
  AbsoluteFill,
  Img,
  Sequence,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import {brand, brandFont} from '../brand';
import {BgGradient} from '../components/BgGradient';
import {EyebrowPill} from '../components/EyebrowPill';
import {InputModeToggle} from '../components/app/InputModeToggle';
import {InputPromptCard} from '../components/app/InputPromptCard';
import {CreateTimerButton} from '../components/app/CreateTimerButton';
import {CreatingStatusLine} from '../components/app/CreatingStatusLine';
import {AppTimerDial, type TimerPhase} from '../components/app/AppTimerDial';
import {PhoneFrame} from '../components/app/PhoneFrame';

export type AdThirtyDemoProps = {
  eyebrow: string;
  parsedWorkoutText: string;
  parsedTitle: string;
  workoutType: string;
  totalRounds: number;
  totalRests: number;
  totalSecondsLabel: string;
  cta: string;
};

const INTRO_FRAMES = 60;
const INPUT_FRAMES = 240;
const CREATING_FRAMES = 60;
const TIMER_RUN_FRAMES = 420;
const CTA_FRAMES = 120;

const SCENE_INTRO_START = 0;
const SCENE_INPUT_START = SCENE_INTRO_START + INTRO_FRAMES;
const SCENE_CREATING_START = SCENE_INPUT_START + INPUT_FRAMES;
const SCENE_TIMER_START = SCENE_CREATING_START + CREATING_FRAMES;
const SCENE_CTA_START = SCENE_TIMER_START + TIMER_RUN_FRAMES;

export const AdThirtyDemo: React.FC<AdThirtyDemoProps> = ({
  eyebrow,
  parsedWorkoutText,
  parsedTitle,
  workoutType,
  totalRounds,
  totalRests,
  totalSecondsLabel,
  cta,
}) => (
  <AbsoluteFill>
    <BgGradient />

    <Sequence from={SCENE_INTRO_START} durationInFrames={INTRO_FRAMES + 12}>
      <IntroScene eyebrow={eyebrow} parsedTitle={parsedTitle} />
    </Sequence>

    <Sequence
      from={SCENE_INPUT_START - 6}
      durationInFrames={INPUT_FRAMES + CREATING_FRAMES + 12}
    >
      <InputScene parsedWorkoutText={parsedWorkoutText} />
    </Sequence>

    <Sequence
      from={SCENE_TIMER_START - 6}
      durationInFrames={TIMER_RUN_FRAMES + 12}
    >
      <TimerScene
        parsedTitle={parsedTitle}
        workoutType={workoutType}
        totalRounds={totalRounds}
        totalRests={totalRests}
        totalSecondsLabel={totalSecondsLabel}
      />
    </Sequence>

    <Sequence from={SCENE_CTA_START - 6} durationInFrames={CTA_FRAMES + 6}>
      <CtaScene cta={cta} parsedTitle={parsedTitle} />
    </Sequence>
  </AbsoluteFill>
);

const IntroScene: React.FC<{eyebrow: string; parsedTitle: string}> = ({
  eyebrow,
  parsedTitle,
}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const enterSpring = spring({
    frame,
    fps,
    from: 0,
    to: 1,
    config: {damping: 14, stiffness: 100},
  });
  const titleScale = interpolate(enterSpring, [0, 1], [0.7, 1]);
  const titleOpacity = interpolate(frame, [0, 14], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const sceneExit = interpolate(
    frame,
    [INTRO_FRAMES - 12, INTRO_FRAMES],
    [1, 0],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );

  return (
    <AbsoluteFill
      style={{
        opacity: sceneExit,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 36,
        padding: '0 60px',
      }}
    >
      <EyebrowPill label={eyebrow} />
      <div
        style={{
          fontFamily: brandFont,
          fontSize: 168,
          fontWeight: 900,
          color: brand.textPrimary,
          letterSpacing: -6,
          textAlign: 'center',
          transform: `scale(${titleScale})`,
          opacity: titleOpacity,
          textShadow: '0 12px 40px rgba(0,0,0,0.55)',
          lineHeight: 0.96,
        }}
      >
        {parsedTitle}
      </div>
      <div
        style={{
          fontFamily: brandFont,
          fontSize: 36,
          fontWeight: 600,
          color: brand.textSecondary,
          letterSpacing: 0.5,
          textAlign: 'center',
          opacity: titleOpacity,
        }}
      >
        in 30 seconds.
      </div>
    </AbsoluteFill>
  );
};

const InputScene: React.FC<{parsedWorkoutText: string}> = ({parsedWorkoutText}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const phoneSpring = spring({
    frame,
    fps,
    from: 0,
    to: 1,
    config: {damping: 14, stiffness: 90},
  });
  const phoneY = interpolate(phoneSpring, [0, 1], [120, 0]);
  const phoneOpacity = interpolate(frame, [0, 16], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const sceneExit = interpolate(
    frame,
    [INPUT_FRAMES + CREATING_FRAMES - 12, INPUT_FRAMES + CREATING_FRAMES],
    [1, 0],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );

  const FILL_START = 40;
  const FILL_END = INPUT_FRAMES - 30;
  const CREATE_AT = INPUT_FRAMES;

  const typedChars = Math.max(
    0,
    Math.floor(
      interpolate(frame, [FILL_START, FILL_END], [0, parsedWorkoutText.length], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
      }),
    ),
  );
  const typedText = parsedWorkoutText.slice(0, typedChars);
  const showCursor = frame < FILL_END && typedChars > 0 && (Math.floor(frame / 8) % 2 === 0);
  const filledOpacity = interpolate(
    frame,
    [FILL_START - 4, FILL_START + 6],
    [0, 1],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );
  const emptyOpacity = 1 - filledOpacity;
  const isCreating = frame >= CREATE_AT;

  const promptContent = typedText.length > 0
    ? `${typedText}${showCursor ? '|' : ''}`
    : '';

  return (
    <AbsoluteFill
      style={{
        opacity: sceneExit,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 60px',
      }}
    >
      <div
        style={{
          opacity: phoneOpacity,
          transform: `translateY(${phoneY}px)`,
        }}
      >
        <PhoneFrame>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              padding: '110px 26px 28px',
              display: 'flex',
              flexDirection: 'column',
              gap: 22,
            }}
          >
            <InputModeToggle selected="write" />
            <InputPromptCard
              filledText={promptContent}
              emptyOpacity={emptyOpacity}
              filledOpacity={filledOpacity}
            />
            <CreateTimerButton isCreating={isCreating} frame={frame} />
            <CreatingStatusLine
              visible={isCreating}
              label="AI is turning your notes into custom timer."
            />
          </div>
        </PhoneFrame>
      </div>

      <SceneCaption
        text={(() => {
          if (frame < FILL_START) return 'Paste your workout.';
          if (frame < CREATE_AT) return 'Any format. Any length.';
          return 'AI builds the timer.';
        })()}
      />
    </AbsoluteFill>
  );
};

const TimerScene: React.FC<{
  parsedTitle: string;
  workoutType: string;
  totalRounds: number;
  totalRests: number;
  totalSecondsLabel: string;
}> = ({parsedTitle, workoutType, totalRounds, totalRests, totalSecondsLabel}) => {
  const frame = useCurrentFrame();

  const enterOpacity = interpolate(frame, [0, 16], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const exitOpacity = interpolate(
    frame,
    [TIMER_RUN_FRAMES - 14, TIMER_RUN_FRAMES],
    [1, 0],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );
  const opacity = Math.min(enterOpacity, exitOpacity);

  const READY_FRAMES = 30;
  const COMPLETE_AT = TIMER_RUN_FRAMES - 30;
  const RUN_LEN = COMPLETE_AT - READY_FRAMES;
  const PER_ROUND = Math.floor(RUN_LEN / totalRounds);
  const WORK_FRACTION = 0.67;
  const workFrames = Math.floor(PER_ROUND * WORK_FRACTION);

  const inReady = frame < READY_FRAMES;
  const inRunning = frame >= READY_FRAMES && frame < COMPLETE_AT;
  const inCompleted = frame >= COMPLETE_AT;

  const within = inRunning ? (frame - READY_FRAMES) % PER_ROUND : 0;
  const isWork = within < workFrames;
  const currentRound = inRunning
    ? Math.min(totalRounds, Math.floor((frame - READY_FRAMES) / PER_ROUND) + 1)
    : inCompleted
      ? totalRounds
      : 0;

  const phaseFrames = isWork ? workFrames : PER_ROUND - workFrames;
  const phaseStart = isWork ? 0 : workFrames;
  const progress = inRunning ? (within - phaseStart) / phaseFrames : inCompleted ? 1 : 0;

  const phaseSecondsTotal = isWork ? 20 : 10;
  const remainingSeconds = inCompleted
    ? 0
    : inRunning
      ? Math.max(0, Math.ceil(phaseSecondsTotal * (1 - progress)))
      : phaseSecondsTotal;

  const phase: TimerPhase = inCompleted ? 'completed' : inReady ? 'ready' : 'running';
  const ringColor = inCompleted
    ? brand.timerComplete
    : isWork
      ? brand.timerWork
      : brand.timerRest;

  return (
    <AbsoluteFill
      style={{
        opacity,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 60px',
      }}
    >
      <div
        style={{
          width: 620,
          maxHeight: 1480,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <PhoneFrame width={620} height={1280}>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              padding: '90px 18px 24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 14,
            }}
          >
            <div
              style={{
                fontFamily: brandFont,
                fontSize: 22,
                fontWeight: 700,
                color: brand.textSecondary,
                letterSpacing: 4,
              }}
            >
              {workoutType}
            </div>
            <div
              style={{
                fontFamily: brandFont,
                fontSize: 44,
                fontWeight: 900,
                color: brand.textPrimary,
                letterSpacing: -1,
              }}
            >
              {parsedTitle}
            </div>
            <AppTimerDial
              type={workoutType}
              progress={progress}
              remainingSeconds={remainingSeconds}
              rounds={currentRound}
              totalRounds={totalRounds}
              restsDone={Math.max(
                0,
                currentRound - (isWork || phase !== 'running' ? 1 : 0),
              )}
              totalRests={totalRests}
              totalSecondsLabel={totalSecondsLabel}
              phase={phase}
              size={420}
              showActions
              ringColor={ringColor}
            />
            <PhaseBadge phase={phase} isWork={isWork} />
          </div>
        </PhoneFrame>
      </div>
    </AbsoluteFill>
  );
};

const PhaseBadge: React.FC<{phase: TimerPhase; isWork: boolean}> = ({phase, isWork}) => {
  const label =
    phase === 'completed' ? 'COMPLETE' : phase === 'ready' ? 'READY' : isWork ? 'WORK' : 'REST';
  const color =
    phase === 'completed'
      ? brand.timerComplete
      : phase === 'ready'
        ? brand.textSecondary
        : isWork
          ? brand.timerWork
          : brand.timerRest;
  return (
    <div
      style={{
        display: 'inline-flex',
        padding: '6px 16px',
        borderRadius: 999,
        background: `${color}22`,
        border: `1.5px solid ${color}66`,
        fontFamily: brandFont,
        fontSize: 16,
        fontWeight: 800,
        color,
        letterSpacing: 3,
      }}
    >
      {label}
    </div>
  );
};

const CtaScene: React.FC<{cta: string; parsedTitle: string}> = ({cta}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const enter = spring({
    frame,
    fps,
    from: 0,
    to: 1,
    config: {damping: 14, stiffness: 100},
  });
  const opacity = interpolate(frame, [0, 14], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const scale = interpolate(enter, [0, 1], [0.85, 1]);

  return (
    <AbsoluteFill
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 32,
        opacity,
        transform: `scale(${scale})`,
        padding: '0 60px',
      }}
    >
      <Img
        src={staticFile('logo.png')}
        style={{
          width: 200,
          height: 200,
          borderRadius: 52,
          boxShadow: `0 20px 80px ${brand.primary}55`,
        }}
      />
      <div
        style={{
          fontFamily: brandFont,
          fontSize: 60,
          fontWeight: 900,
          color: brand.textPrimary,
          letterSpacing: -1.2,
          textAlign: 'center',
          whiteSpace: 'pre-line',
          lineHeight: 1.05,
        }}
      >
        {'AI WOD Timer'}
      </div>
      <div
        style={{
          padding: '22px 50px',
          borderRadius: 26,
          background: brand.primaryLight,
          fontFamily: brandFont,
          fontSize: 30,
          fontWeight: 800,
          color: brand.textPrimary,
          letterSpacing: 0.5,
          boxShadow: `0 20px 50px ${brand.primary}66`,
        }}
      >
        {cta}
      </div>
    </AbsoluteFill>
  );
};

const SceneCaption: React.FC<{text: string}> = ({text}) => (
  <div
    style={{
      position: 'absolute',
      bottom: 140,
      left: 0,
      right: 0,
      fontFamily: brandFont,
      fontSize: 42,
      fontWeight: 800,
      color: brand.textPrimary,
      textAlign: 'center',
      letterSpacing: -0.5,
      textShadow: '0 8px 30px rgba(0,0,0,0.7)',
    }}
  >
    {text}
  </div>
);
